/* eslint-disable react-hooks/exhaustive-deps */
import { MoveLeft } from "lucide-react";
import moment from "moment-timezone";
import React from "react";
import { useTranslation } from "react-i18next";
import DropDownList from "../../components/DropDownList";
import { Icons } from "../../components/icons";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import fetchData from "../../utils/api";
import { Config } from "../../utils/config";
import { dateFormat } from "../../utils/date-format";
import { formatArrivingTime } from "../../utils/format-arriving-time";
import OrderDetails from "../place-order/OrderDetails";
import TableReservationDetails from "../place-order/TableReservationDetails";

export default function History({
  authToken,
  id,
  restaurantDetail,
  ssoUserDetails,
  setCurrentSelected,
  setCartTotal,
}) {
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isOrderAgainLoading, setIsOrderAgainLoading] =
    React.useState<boolean>(false);
  const [orderHistory, setOrderHistory] = React.useState([]);
  const [noOrderHistory, setNoOrderHistory] = React.useState<boolean>(false);
  const [isOrderDetail, setIsOrderDetails] = React.useState(false);
  const [orderDetail, setOrderDetail] = React.useState<any>({});
  const [taxLabel, setTaxLabel] = React.useState<any>();
  const [selectedType, setSelectedType] = React.useState("all");
  const [filteredOrderHistory, setFilteredOrderHistory] = React.useState([]);
  const [timeLeft, setTimeLeft] = React.useState(0);
  const [isRunning, setIsRunning] = React.useState(true);
  const cachedCart = localStorage.getItem("cart");

  const handleOrderDetails = ({ order }) => {
    const orderingMethod =
      order.orderingMethod === "dine_in"
        ? "in_restaurant"
        : order.orderingMethod;

    if (order.orderingMethod !== "table_reservation") {
      setTaxLabel(
        order?.taxDetails ??
          Array?.from(
            order?.orderDetails
              ?.reduce((map, item) => {
                if (item.tax_category[orderingMethod] !== undefined) {
                  const taxName = item.tax_category.tax_category;
                  const taxRate = item.tax_category[orderingMethod];
                  const totalTax = (item.countTotal * Number(taxRate)) / 100;

                  if (map.has(taxName)) {
                    // If the tax name already exists, add the totalTax to the existing value
                    map.get(taxName).totalTax += totalTax;
                  } else {
                    // If the tax name does not exist, create a new entry
                    map.set(taxName, {
                      name: taxName,
                      tax: taxRate,
                      totalTax: totalTax,
                    });
                  }
                }
                return map;
              }, new Map())
              .values()
          )
      );
    }
    setIsOrderDetails(true);
    setOrderDetail(order);
  };

  const handleOrderAgain = async ({ order_id }) => {
    try {
      setIsOrderAgainLoading(true);
      const body: any = {
        order_id: order_id,
        cart_id: cachedCart,
      };
      const res = await fetchData({
        url: "orders/order-again",
        method: "POST",
        body,
        token: authToken,
      });
      if (res.status_code === 200) {
        setCurrentSelected("cart");
      }
      setIsOrderAgainLoading(false);

      const bodyData = {
        cart_id: cachedCart,
      };
      const cartItemTotal = await fetchData({
        url: "cart/total",
        method: "POST",
        body: bodyData,
        token: authToken,
      });
      if (cartItemTotal.status_code === 200) {
        setCartTotal(cartItemTotal.data);
      }
    } catch (error) {
      console.error("error", error);
    }
  };

  const handleFetchOrderDetails = async () => {
    try {
      setIsLoading(true);
      const body: any = {
        type: "email",
        email: ssoUserDetails.email,
      };
      const res = await fetchData({
        url: "orders/history",
        method: "POST",
        body,
        token: authToken,
      });
      if (res.status_code === 200) {
        if (res.message === "order_history.error") {
          setNoOrderHistory(true);
          setOrderHistory([]);
          setFilteredOrderHistory([]);
          setIsOrderDetails(false);
        } else {
          setIsOrderDetails(false);
          setNoOrderHistory(res.data.length === 0 ? true : false);
          setOrderHistory(res.data);
          setFilteredOrderHistory(res.data);
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error("error", error);
    }
  };

  const handleBackToHistory = () => {
    setIsOrderDetails(false);
    setOrderDetail({});
  };

  React.useEffect(() => {
    if (ssoUserDetails) {
      handleFetchOrderDetails();
    }
  }, [ssoUserDetails]);

  const handleOrderMethod = ({ method }) => {
    if (method === "all") {
      setFilteredOrderHistory(orderHistory);
    } else {
      const updated = orderHistory.filter(
        (order: any) => order.orderingMethod === method
      );

      setFilteredOrderHistory(updated);
    }
  };

  React.useEffect(() => {
    const currentTime = moment().tz(restaurantDetail?.timezone);
    const orderFulFillmentTime = moment(orderDetail?.fulfillmentTime).tz(
      restaurantDetail?.timezone
    );
    if (orderFulFillmentTime.isAfter(currentTime)) {
      // Calculate the time difference in seconds
      const timeDifferenceInSeconds = orderFulFillmentTime.diff(
        currentTime,
        "seconds"
      );

      // Output the result
      setTimeLeft(timeDifferenceInSeconds);
    } else {
      setIsRunning(false);
      setTimeLeft(0);
    }
  }, [orderDetail]);

  React.useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  const handlePullToRefresh = async () => {
    if (ssoUserDetails) {
      handleFetchOrderDetails();
    }
    setIsRefreshing(true);
    // handleMenuList();
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsRefreshing(false);
  };

  let initialTouchY = 0;

  return (
    <div className="container">
      <div className="space-y-10">
        {isOrderDetail ? (
          <div className="p-4 bg-white rounded-lg space-y-4">
            <div className="flex items-center gap-3">
              <MoveLeft
                className="flex-shrink-0 size-5 text-red cursor-pointer"
                onClick={() => {
                  handleBackToHistory();
                }}
              />
              <h3 className="text-red font-semibold text-lg sm:text-xl">
                {t("place_order.order_details")}
              </h3>
            </div>
            <Separator />
            <div>
              <div className="flex w-full justify-between">
                <div className="flex w-full justify-start text-start mb-4">
                  {(orderDetail?.orderingMethod !== "delivery" ||
                    orderDetail?.orderingMethod !== "pickup") &&
                    (orderDetail?.status === "accepted" ||
                      orderDetail?.status === "ready") &&
                    timeLeft > 0 && (
                      <div>
                        <div>
                          <Label className="font-bold">
                            {t("place_order.arriving_time")}
                          </Label>
                        </div>
                        <div>
                          <Label>{formatArrivingTime(timeLeft)}</Label>
                        </div>
                      </div>
                    )}
                </div>
                <div className="flex w-full justify-end text-end mb-4">
                  <div>
                    <p className="text-sm leading-none font-bold text-right">
                      {t("order_number")}:{" "}
                      <span className="font-medium">{orderDetail.id}</span>
                    </p>
                    <div>
                      <Label className="font-bold">
                        {t("place_order.order_placed")}
                      </Label>
                    </div>
                    <div>
                      <Label>
                        {dateFormat({
                          date: orderDetail.createdAt,
                          timezone: restaurantDetail.timezone,
                        })}
                      </Label>
                    </div>
                  </div>
                </div>
              </div>

              {orderDetail?.orderingMethod === "table_reservation" ? (
                <TableReservationDetails
                  cartList={orderDetail}
                  authToken={authToken}
                />
              ) : (
                <OrderDetails
                  taxLabel={taxLabel}
                  cartList={orderDetail}
                  subtotal={orderDetail.subTotal}
                  total={orderDetail.totalAmount}
                  couponCode={orderDetail.couponCode}
                  couponData={orderDetail.couponDetails}
                  couponDiscount={orderDetail.couponDiscount}
                  restaurantDetail={restaurantDetail}
                  deliveryFees={orderDetail.deliveryFees}
                  deliveryFeesTax={orderDetail.deliveryFeesTax}
                  orderingMethod={orderDetail.orderingMethod}
                  serviceFeesLabel={orderDetail.serviceTaxDetails}
                  tipTaxFees={parseFloat(orderDetail?.tipTaxFee)}
                  totalTipAmount={orderDetail?.totalTip}
                  authToken={authToken}
                  roundOff={orderDetail.roundOff}
                />
              )}
            </div>
          </div>
        ) : (
          orderHistory.length > 0 && (
            <div
              className="flex flex-col gap-4 sm:gap-6"
              onTouchStart={(e) => {
                initialTouchY = e.touches[0].clientY; // Record the initial touch position
              }}
              onTouchMove={(e) => {
                // Only trigger the refresh if the user has pulled down
                const currentTouchY = e.touches[0].clientY;
                if (
                  currentTouchY > initialTouchY &&
                  currentTouchY - initialTouchY > 50 &&
                  !isRefreshing
                ) {
                  handlePullToRefresh();
                }
              }}
            >
              <div
                className={` flex items-center justify-center bg-white rounded-lg transition-all duration-300 ${
                  isRefreshing
                    ? "h-[60px] opacity-100"
                    : "h-0 hidden opacity-0 pointer-events-none"
                }`}
              >
                {isRefreshing ? (
                  <div className="h-6 w-6 text-primary">
                    <Icons.spinner className="size-5 animate-spin" />
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
                <h2 className="font-semibold text-xl sm:text-3xl">
                  {t("order_history.title")}
                </h2>
                <div className="ml-auto max-w-[200px] sm:max-w-[250px] w-full">
                  <DropDownList
                    id="type"
                    className={"max-w-[300px] w-fit"}
                    label={t("cart.ordering_method.title")}
                    data={Config.env.app.ordering_method_history}
                    value={selectedType}
                    onValueChange={(e: any) => {
                      setSelectedType(e);

                      handleOrderMethod({ method: e });
                    }}
                  />
                </div>
              </div>
              {filteredOrderHistory.length > 0 && (
                <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                  {filteredOrderHistory.map((item: any, index: number) => {
                    const orderStatus: any = Config.env.app.order_status.find(
                      (status) => status.value === item.status
                    );
                    const paymentMethod: any =
                      Config.env.app.payment_method.find(
                        (status) => status.value === item.paymentMethod
                      );
                    return (
                      <div
                        className="p-5 bg-white rounded-lg shadow-green-50 space-y-4"
                        key={index}
                      >
                        <div className="border-b pb-4">
                          <h4 className="text-green-900 text-xl font-semibold">
                            {item?.restaurants?.name} -{" "}
                            {t(
                              Config.env.app.ordering_method.find(
                                (orderingItem) =>
                                  orderingItem.value === item?.orderingMethod
                              )?.label as string
                            )}
                          </h4>
                        </div>
                        <div className="w-full flex flex-wrap justify-between gap-4 text-green-900">
                          <div className="space-y-1">
                            <h6 className="text-sm font-semibold">Date</h6>
                            <p className="text-xs md:max-w-[115px]">
                              {dateFormat({
                                date: item.createdAt,
                                timezone: restaurantDetail.timezone,
                              })}
                            </p>
                          </div>
                          <div className="flex justify-between gap-4 sm:gap-6">
                            <div className="space-y-1">
                              <h6 className="text-sm font-semibold whitespace-nowrap">
                                {t("order_history.order_status")}
                              </h6>

                              <div className="inline-flex">
                                <p className={`${t(orderStatus?.color)}`}>
                                  {t(orderStatus?.label)}
                                </p>
                              </div>
                            </div>
                            {item.orderingMethod !== "table_reservation" && (
                              <div className="space-y-1">
                                <h6 className="text-sm font-semibold whitespace-nowrap">
                                  {t("order_history.payment_method")}{" "}
                                </h6>

                                <p className={`${t(paymentMethod?.color)}`}>
                                  {t(paymentMethod?.label)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          {item?.orderingMethod !== "table_reservation" && (
                            <Button
                              className="p-0 underline !h-auto text-red text-sm font-medium"
                              variant={"link"}
                              onClick={() => {
                                handleOrderAgain({ order_id: item.id });
                              }}
                              disabled={isOrderAgainLoading}
                            >
                              {t("place_order.order_again")}
                            </Button>
                          )}
                          <div className="flex w-full justify-end">
                            <Button
                              className="p-0 underline !h-auto text-red text-sm font-medium"
                              variant={"link"}
                              onClick={() => {
                                handleOrderDetails({ order: item });
                              }}
                            >
                              {t("place_order.order_details")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )
        )}
        {noOrderHistory && (
          <Card>
            <CardContent>
              <div className="flex justify-center">
                <Label>{t("order_history.error")}</Label>
              </div>
            </CardContent>
          </Card>
        )}
        {isLoading && (
          <div className="h-[70dvh] flex justify-center items-center flex-col">
            <Icons.spinner className="size-5 animate-spin" />
            <h2>{t("please_wait")}</h2>
            <p className="text-sm mt-2 text-center">
              {t("order_history.did_you_know")}
            </p>
          </div>
        )}
        {!ssoUserDetails && (
          <Card>
            <CardContent>
              <div className="flex justify-center">
                <Button
                  onClick={() =>
                    (window.location.href = `${Config.env.app.sso_url}/sign-in/?return_url=${window.location.href}&return_token=true`)
                  }
                >
                  {t("login")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
